import React, { useContext, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAuth } from './auth';
import { useNavigate } from 'react-router-dom';
import SendNotificationModal from './admin/SendNotificationModal';

const CommunicationCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const CommunicationCenter: React.FC = () => {
  const { IsAdmin } = useContext(useAuth);
  const navigate = useNavigate();
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography>
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 10 }}>
      <Typography variant="h4" gutterBottom>
        Communication Center
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Manage communications with your congregation through email, text messages, and site notifications.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6} lg={3}>
          <CommunicationCard>
            <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
              <IconWrapper>
                <EmailIcon sx={{ fontSize: 60 }} />
              </IconWrapper>
              <Typography variant="h5" gutterBottom>
                Email Communications
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Send emails to your congregation members. Create and manage email templates.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate('/send-email')}
              >
                Manage Emails
              </Button>
            </Box>
          </CommunicationCard>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <CommunicationCard>
            <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
              <IconWrapper>
                <SmsIcon sx={{ fontSize: 60 }} />
              </IconWrapper>
              <Typography variant="h5" gutterBottom>
                Text Messages
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Send text messages to your congregation. Manage phone numbers and groups.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate('/send-sms')}
              >
                Manage Texts
              </Button>
            </Box>
          </CommunicationCard>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <CommunicationCard>
            <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
              <IconWrapper>
                <NotificationsActiveIcon sx={{ fontSize: 60 }} />
              </IconWrapper>
              <Typography variant="h5" gutterBottom>
                Site Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create and manage site-wide notifications and announcements.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setNotificationModalOpen(true)}
              >
                Manage Notifications
              </Button>
            </Box>
          </CommunicationCard>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <CommunicationCard>
            <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
              <IconWrapper>
                <Box sx={{ position: 'relative' }}>
                  <EmailIcon sx={{ fontSize: 60 }} />
                  <Box sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'background.paper', borderRadius: '50%' }}>
                    <SmsIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                  </Box>
                </Box>
              </IconWrapper>
              <Typography variant="h5" gutterBottom>
                List Management
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Manage mailing lists and individual recipients. Add, edit, or delete lists and members.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, mt: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate('/admin/list-management')}
              >
                Manage Lists
              </Button>
            </Box>
          </CommunicationCard>
        </Grid>
      </Grid>


      <SendNotificationModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
      />
    </Box>
  );
};

export default CommunicationCenter; 